import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/cn";
import { Input } from "../Input";
import type { InputProps } from "../Input";

export interface PasswordFieldProps extends Omit<InputProps, "type"> {
  hidePasswordLabel: string;
  showPasswordLabel: string;
  containerClassName?: string;
}

export function PasswordField({
  className,
  containerClassName,
  hidePasswordLabel,
  showPasswordLabel,
  disabled,
  ref,
  ...props
}: PasswordFieldProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleToggle = () => {
    const input = inputRef.current;
    const start = input?.selectionStart;
    const end = input?.selectionEnd;

    setShowPassword((prev) => !prev);

    if (
      input &&
      start !== null &&
      start !== undefined &&
      end !== null &&
      end !== undefined
    ) {
      requestAnimationFrame(() => {
        input.setSelectionRange(start, end);
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref && "current" in ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  return (
    <div
      className={cn("relative flex w-full items-center", containerClassName)}
    >
      <Input
        ref={setRefs}
        type={showPassword ? "text" : "password"}
        disabled={disabled}
        className={cn("pe-10", className)}
        {...props}
      />
      <button
        type="button"
        aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
        title={showPassword ? hidePasswordLabel : showPasswordLabel}
        onClick={handleToggle}
        onMouseDown={handleMouseDown}
        disabled={disabled}
        className="absolute end-0 flex h-full items-center px-3 text-secondary-icon transition-colors hover:text-text focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-e-md disabled:cursor-not-allowed disabled:opacity-50"
      >
        {showPassword ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
